// pages/Doubts.jsx
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Chip,
  Avatar,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import {
  AccountCircle as AccountCircleIcon,
  FindInPage as LostFoundIcon,
  Lightbulb as SkillsIcon,
  Help as DoubtsIcon,
  Settings as SettingsIcon,
  Event as EventIcon
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import api from "../../api";

import LogoutNavbar from "../../components/LogoutNavbar";
import PostLoginFooter from "../../components/PostLoginFooter";
import GlobalSidebar from "../../components/GlobalSidebar";
import BackButton from "../../components/BackButton";

export default function Doubts({ embedded = false } = {}) {
  const footerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [doubts, setDoubts] = useState([]);
  const [selectedDoubt, setSelectedDoubt] = useState(null);
  const [newQuestion, setNewQuestion] = useState("");
  const [commentText, setCommentText] = useState("");
  const commentsEndRef = useRef(null);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Define sidebar items to pass to Navbar
  const sidebarItems = [
    { 
      label: 'Profile', 
      path: '/login/student/home', 
      action: () => navigate('/login/student/home'),
      icon: <AccountCircleIcon />
    },
    { 
      label: 'Lost and Found', 
      path: '/lostfound', 
      action: () => navigate('/lostfound'),
      icon: <LostFoundIcon />
    },
    { 
      label: 'Skills', 
      path: '/skills', 
      action: () => navigate('/skills'),
      icon: <SkillsIcon />
    },
    { 
      label: 'Doubts', 
      path: '/yourdoubts', 
      action: () => navigate('/yourdoubts'),
      icon: <DoubtsIcon />,
      active: true
    },
    { 
      label: 'Settings', 
      path: '/settings', 
      action: () => navigate('/settings'),
      icon: <SettingsIcon />
    },
    { 
      label: 'Events', 
      path: '/events', 
      action: () => navigate('/events'),
      icon: <EventIcon />
    },
  ];

  const fetchdoubt = async () => {
    try {
      const response = await api.get('doubts/createview/');
      // console.log(response);
      setDoubts(response.results.data || []);
    } catch (error) {
      // console.log('Error fetching doubts:', error);
      setSnackbarMessage("Failed to load doubts");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Add new doubt
  const handleAddDoubt = async () => {
    if (!newQuestion.trim()) {
      setSnackbarMessage("Please enter a question");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }
    
    try {
      const response = await api.post('doubts/createview/', {
        question: newQuestion,
      });
      
      if (response.data && response.data.user) {
        // Use the doubt data returned from the backend (includes ID, timestamps, etc.)
        setDoubts([response.data, ...doubts]);
        setNewQuestion("");
        
        setSnackbarMessage("Doubt created successfully");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        fetchdoubt();
      }
    } catch (error) {
      console.error("Error creating doubt:", error);
      setSnackbarMessage("Failed to create doubt");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Add comment
  const handleAddComment = async () => {
    if (!commentText.trim()) {
      setSnackbarMessage("Please enter a comment");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }
    
    try {
      const response = await api.post(`doubts/comments/${selectedDoubt.id}`, {
        comment: commentText,
      });
      
      if (response.data) {
        const updatedDoubt = {
          ...selectedDoubt,
          comments: [...(selectedDoubt.comments || []), response.data]
        };
        setSelectedDoubt(updatedDoubt);
        setSnackbarMessage("Comment added successfully");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setCommentText("");
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      setSnackbarMessage("Failed to add comment");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleCopyAnswer = () => {
    navigator.clipboard.writeText(selectedDoubt.ai_suggestions)
      .then(() => {
        setSnackbarMessage("Answer copied to clipboard");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      })
      .catch(() => {
        setSnackbarMessage("Failed to copy answer");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      });
  };

  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selectedDoubt]);

  useEffect(() => {
    fetchdoubt();
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: embedded ? "auto" : "100vh" }}>
      {!embedded && <LogoutNavbar sidebarItems={isMobile ? sidebarItems : []} />}
      <Box sx={{ display: "flex", flexGrow: 1 }}>
        {/* Only show sidebar on desktop */}
        {!embedded && !isMobile && <GlobalSidebar />}
        
        <Box component="main" sx={{ 
          flexGrow: 1, 
          overflowX: "hidden",
          bgcolor: embedded ? 'transparent' : "#f5f7fa"
        }}>
          <Box sx={{ p: embedded ? 0 : { xs: 2, md: 4 } }}>
            {!embedded && <BackButton sx={{ mb: 2 , backgroundColor: "#dfdfdff", color: "#000" }} />}
            {!selectedDoubt ? (
              <>
                <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: "1.8rem", md: "2.5rem" }, background: "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  Doubts
                </Typography>

                {/* Add New Question */}
                <Paper
                  sx={{
                    p: 2,
                    mb: 3,
                    display: "flex",
                    gap: 2,
                    borderRadius: 3,
                    bgcolor: "rgba(255,255,255,0.9)",
                    backdropFilter: "blur(6px)",
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Ask a new doubt..."
                    value={newQuestion}
                    onChange={(e) => setNewQuestion(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleAddDoubt()}
                  />
                  <Button
                    variant="contained"
                    sx={{ borderRadius: 2, minWidth: { xs: "100%", sm: "auto" } ,  background: "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)",
    color: "#fff", }}
                    onClick={handleAddDoubt}
                  >
                   POST
                  </Button>
                </Paper>

                {/* Doubts List */}
                <Box sx={{ display: "grid", gap: 2 }}>
                  {doubts.map((doubt) => (
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      key={doubt.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <Paper
                        elevation={4}
                        sx={{
                          p: 3,
                          borderRadius: 3,
                          cursor: "pointer",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          bgcolor: "rgba(255,255,255,0.85)",
                          backdropFilter: "blur(6px)",
                        }}
                        onClick={() => setSelectedDoubt(doubt)}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: 'wrap', gap: 1 }}>
                          <Typography variant="h6" fontWeight="bold" sx={{ flex: 1, minWidth: 200, background: "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                            {doubt.question}
                          </Typography>
                          <Chip
                            label={doubt.status}
                            color={doubt.status === "open" ? "success" : "warning"}
                            size="small"
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Posted by {doubt.user.username} • {new Date(doubt.created_at).toLocaleDateString()}
                        </Typography>
                      </Paper>
                    </motion.div>
                  ))}
                </Box>
              </>
            ) : (
              <>
                

                {/* Selected Doubt */}
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    mb: 3,
                    bgcolor: "rgba(255,255,255,0.9)",
                    backdropFilter: "blur(6px)",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, background: "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                      {selectedDoubt.question}
                    </Typography>
                    <Chip
                      label={selectedDoubt.status}
                      color={
                        selectedDoubt.status === "open" ? "success" : "warning"
                      }
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    By {selectedDoubt.user.username} • {selectedDoubt.created_at}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  {/* AI Answer */}
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: "#e3f2fd",
                      borderRadius: 2,
                      position: "relative",
                    }}
                    elevation={1}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      AI Suggested Answer
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      {selectedDoubt.ai_suggestions}
                    </Typography>
                    <IconButton
                      size="small"
                      sx={{ position: "absolute", top: 8, right: 8 }}
                      onClick={handleCopyAnswer}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                </Paper>

                {/* Comments */}
                <Typography variant="h6" gutterBottom>
                  Comments :
                </Typography>
                <Box sx={{ mb: 2 }}>
                  {selectedDoubt.comments && selectedDoubt.comments.map((comment) => (
                    <Paper
                      key={comment.id}
                      sx={{
                        p: 2,
                        mb: 1,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        borderRadius: 2,
                        bgcolor: "rgba(250,250,250,0.9)",
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      <Avatar sx={{ bgcolor: "#1976d2" }}>{comment.author.username[0]}</Avatar>
                      <Box>
                        <Typography variant="subtitle2">{comment.author.username}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {comment.comment}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {new Date(comment.created_at).toLocaleString()}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                  <div ref={commentsEndRef} />
                </Box>

                {/* Add Comment */}
                <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Add a comment..."
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleAddComment()}
                    sx={{ borderRadius: 3 }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddComment}
                    sx={{ borderRadius: 3, minWidth: { xs: "100%", sm: "auto" } ,  background: "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)",
    color: "#fff",}}
                  >
                    POST
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {!embedded && (
        <div ref={footerRef}>
          <PostLoginFooter />
        </div>
      )}
    </Box>
  );
}